import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiLock, FiShield, FiKey, FiEye, FiFileText, FiUsers, FiDatabase, FiArrowRight } = FiIcons;

const InformationProtectionPage = () => {
  const protectionTypes = [
    {
      icon: FiLock,
      title: 'Encryption Services',
      description: 'Advanced encryption to protect data at rest, in transit, and in use',
      features: [
        'AES-256 encryption standards',
        'Automatic key rotation',
        'Hardware security module (HSM) integration',
        'Cloud and on-premises encryption',
        'Zero-knowledge encryption options'
      ],
      level: 'Military-Grade'
    },
    {
      icon: FiShield,
      title: 'Rights Management',
      description: 'Control access to sensitive information with granular permissions',
      features: [
        'Document access control',
        'Permission-based sharing',
        'Time-limited access',
        'Revocation capabilities',
        'Watermarking and tracking'
      ],
      level: 'Enterprise-Level'
    },
    {
      icon: FiKey,
      title: 'Key Management',
      description: 'Secure lifecycle management for all encryption keys and certificates',
      features: [
        'Centralized key repository',
        'Automated key provisioning',
        'Secure key distribution',
        'Compliance reporting',
        'Disaster recovery support'
      ],
      level: 'FIPS 140-2'
    },
    {
      icon: FiEye,
      title: 'Classification & Labeling',
      description: 'Automated data classification and sensitivity labeling',
      features: [
        'AI-powered classification',
        'Custom label creation',
        'Policy-based labeling',
        'User training integration',
        'Audit trail maintenance'
      ],
      level: 'AI-Enhanced'
    }
  ];

  const features = [
    { icon: FiFileText, title: 'Document Protection', description: 'Comprehensive protection for all document types and formats' },
    { icon: FiUsers, title: 'User Access Control', description: 'Granular access controls based on roles and permissions' },
    { icon: FiDatabase, title: 'Data Masking', description: 'Dynamic data masking for non-production environments' },
    { icon: FiShield, title: 'Threat Protection', description: 'Advanced threat detection and response capabilities' }
  ];

  const benefits = [
    { metric: '99.9%', description: 'Data protection success rate' },
    { metric: '80%', description: 'Reduction in data exposure' },
    { metric: '90%', description: 'Faster incident response' },
    { metric: '70%', description: 'Improved compliance posture' }
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-900 to-indigo-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-6">
                Enterprise Information Protection
              </h1>
              <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
                Safeguard your organization's most valuable information with comprehensive protection solutions powered by Microsoft Purview.
              </p>
              <Link
                to="/purview-audit"
                className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105"
              >
                Get Purview Protection Assessment
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Uncompromising Information Security
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our information protection solutions deliver enterprise-grade security with measurable results
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-purple-600 mb-2">{benefit.metric}</div>
                <div className="text-gray-600">{benefit.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Protection Types Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Comprehensive Protection Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multi-layered security approach to protect all types of sensitive information
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {protectionTypes.map((protection, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-lg shadow-lg"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <SafeIcon icon={protection.icon} className="h-8 w-8 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{protection.title}</h3>
                    <p className="text-gray-600 mb-2">{protection.description}</p>
                    <span className="inline-block bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {protection.level}
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {protection.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <SafeIcon icon={FiShield} className="text-purple-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Advanced Protection Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge capabilities to secure your most sensitive information
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <SafeIcon icon={feature.icon} className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Secure Your Sensitive Information
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Let our experts help you build a comprehensive information protection strategy
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/purview-audit"
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105"
            >
              Get Purview Protection Assessment
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InformationProtectionPage;